import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { userContext } from "../../stores/users/userContext";
import apiRequest from "../../utils/apiClient";
import { resolveShareUrl, shareSrtUrl, staffPassUrl } from "../constants";
import { STORAGE_KEYS } from "../../constants";
import { getLocalStorage } from "../../utils";
import CreateVideo from "./createVideo";
import AskAiPanel from "./AskAiPanel";

/**
 * Public shared-ET page (route /s/:token). Resolves the share token to a transcript,
 * stashes the token (so all ET data/export calls carry it via apiClient), bridges the
 * transcript id into userStore so the existing ET loads it, then renders the editor in
 * "shared mode". No login required.
 */
const SharedCreateVideo = () => {
  const { token } = useParams();
  const userStore = useContext(userContext);
  const [status, setStatus] = useState("loading"); // 'loading' | 'ready' | 'error'
  const [message, setMessage] = useState("");
  // ?ask=1 (the notetaker email's "Copy the transcript"): the copy-for-AI page.
  const [askOpen, setAskOpen] = useState(() => new URLSearchParams(window.location.search).get("ask") === "1");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Store the token first so the ET's subsequent data/export requests carry it.
        let staffCode = null;
        try {
          sessionStorage.setItem("cc_share_token", token);
          // index.html moved the admin "Open ET" link's one-minute code here from the URL.
          if (sessionStorage.getItem("cc_staff_code_for") === token) staffCode = sessionStorage.getItem("cc_staff_code");
          sessionStorage.removeItem("cc_staff_code");
          sessionStorage.removeItem("cc_staff_code_for");
        } catch (e) { /* ignore */ }
        // Trade it for this tab's staff pass (it comes back in the body, never in a URL).
        if (staffCode) {
          const r = await apiRequest(staffPassUrl(token), { method: "POST", headers: { "X-Staff-Code": staffCode } });
          const b = await r.json().catch(() => ({}));
          if (r.ok && b.pass) {
            try {
              sessionStorage.setItem("cc_staff_pass", b.pass);
              sessionStorage.setItem("cc_staff_pass_for", token);
            } catch (e) { /* ignore */ }
          }
        }
        try {
          if (sessionStorage.getItem("cc_staff_pass_for") === token) sessionStorage.setItem("cc_share_staff", "1");
          else sessionStorage.removeItem("cc_share_staff");
        } catch (e) { /* ignore */ }
        let res = await apiRequest(resolveShareUrl(token), { method: "GET" });
        let body = await res.json().catch(() => ({}));
        // A staff-only link: ask again with the visitor's own login attached (apiClient adds
        // it once cc_share_staff is set). The backend decides whether that login is staff.
        if (res.status === 403 && body.staff_only && sessionStorage.getItem("cc_share_staff") !== "1"
            && getLocalStorage(STORAGE_KEYS.TOKEN)) {
          try { sessionStorage.setItem("cc_share_staff", "1"); } catch (e) { /* ignore */ }
          res = await apiRequest(resolveShareUrl(token), { method: "GET" });
          body = await res.json().catch(() => ({}));
        }
        if (cancelled) return;
        if (res.ok && body.valid) {
          try {
            sessionStorage.setItem("cc_share_perm", body.permission_level || "export");
            // Staff links only: whose video this is, so exports can say where they land.
            if (body.owner_email) sessionStorage.setItem("cc_share_owner", body.owner_email);
            else sessionStorage.removeItem("cc_share_owner");
          } catch (e) { /* ignore */ }
          userStore.setUserTranscript(null, body.title || "Shared clips", body.transcript_id, 0);
          setStatus("ready");
        } else {
          try {
            sessionStorage.removeItem("cc_share_token");
            sessionStorage.removeItem("cc_share_perm");
            sessionStorage.removeItem("cc_share_staff");
          } catch (e) { /* ignore */ }
          setMessage(body.message || "This shared link isn't valid.");
          setStatus("error");
        }
      } catch (e) {
        if (!cancelled) {
          setMessage("Couldn't open this shared link. Please try again.");
          setStatus("error");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token, userStore]);

  // ?srt=1 (the notetaker email's "Download the subtitles"): fetch the .srt with the share
  // token (apiClient adds it) and hand it to the browser as a download, then open the ET.
  useEffect(() => {
    if (status !== "ready" || new URLSearchParams(window.location.search).get("srt") !== "1") return;
    (async () => {
      try {
        const res = await apiRequest(shareSrtUrl, { method: "GET" });
        if (!res.ok) return;
        const name = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") || "")?.[1] || "call.srt";
        const url = URL.createObjectURL(await res.blob());
        const a = Object.assign(document.createElement("a"), { href: url, download: name });
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      } catch (e) { /* the ET still opens; the subtitles are a convenience */ }
    })();
  }, [status]);

  if (status === "loading") {
    return (
      <div style={{ minHeight: "100vh", background: "#0d0d0d", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Outfit, sans-serif" }}>
        Opening shared clips…
      </div>
    );
  }
  if (status === "error") {
    return (
      <div style={{ minHeight: "100vh", background: "#0d0d0d", color: "#fff", display: "flex", flexDirection: "column", gap: "8px", alignItems: "center", justifyContent: "center", fontFamily: "Outfit, sans-serif", textAlign: "center", padding: "24px" }}>
        <div style={{ fontSize: "20px", fontWeight: 700 }}>🍒 CherryClipz</div>
        <div style={{ color: "#bbb" }}>{message}</div>
      </div>
    );
  }
  // ?ask=1 is a page of its own (just the copy button), so nothing from the ET sits on top of
  // it; "Open the clips" drops the flag and shows the ET.
  if (askOpen) return <AskAiPanel page onClose={() => setAskOpen(false)} />;
  return <CreateVideo />;
};

export default SharedCreateVideo;
